import React, { useState, useEffect } from "react"
import { useBlocks } from "app/regions/hooks/use-blocks"
import { Block } from "../shared/block"
import { useFetch } from "hooks/use-new-fetch"
import { ProductEmbedMarkup } from "./product-embed-markup"

// This is the component that's rendered in the editor.
export function ProductEmbedEditor({ id }) {
  // We can access the properties the user sets in the sidebar with the
  // `properties` object. We'll use this to store data that should be persisted
  // such as the selected product id, button text, whether or not to show the
  // description, etc.
  const { properties } = useBlocks(id)

  const [data, setData] = useState({})
  const [request] = useFetch(`/editor/products`)

  // When the product id changes, make a request to the server to grab that
  // product's data, and set it in the local state. We only use this to make the
  // UI look nice for the user — e.g. to show them "Authory" instead of the
  // Liquid. All of this will be replaced with Liquid tags at save time via
  // product-embed-email.js.
  useEffect(() => {
    if (!properties.productId) return

    async function fetchProductData() {
      let queryParams = {}
      if (properties.includeDiscount && properties.discountCode) {
        queryParams.coupon_code = properties.discountCode
      }
      if (properties.goDirectlyToCheckout) {
        queryParams.link_to_checkout = true
      }

      const { response, data } = await request.get({
        path: `/${properties.productId}`,
        queryParams
      })

      if (response.ok) {
        const productDetails = {
          imageUrl: data.product.image,
          imageAlt: `Image for ${data.product.title}`,
          price: data.product.price,
          title: data.product.title,
          description: data.product.description,
          url: data.product.url,
          buttonText: data.product.button_text
        }
        setData(productDetails)
      } else {
        setData({})
      }
    }

    fetchProductData()
  }, [
    properties.productId,
    properties.includeDiscount,
    properties.discountCode,
    properties.goDirectlyToCheckout
  ])

  // Return a placeholder if the user hasn't entered a product.
  if (!properties.productId) {
    return (
      <Block
        id={id}
        style={{
          margin: "24px 0",
          paddingLeft: 16,
          border: "1px solid var(--gray-100)"
        }}
      >
        <p>Choose a product in the sidebar to get started.</p>
      </Block>
    )
  }

  return (
    // The reason we're using <Block> here (and not just a plain <div>) is that
    // <Block> focuses the sidebar when it's clicked. This allows us to render
    // the settings component from product-embed-settings.js.
    <Block id={id}>
      <ProductEmbedMarkup properties={properties} productData={data} />
    </Block>
  )
}
